import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AppService } from '../../app.service';
import { UserAdminService } from '../user-admin.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../shared/_services/json-object-factory.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { UtilityService } from '../../shared/_services/utility.service';
import { UserAssignModalComponent } from '../user-assign-modal/user-assign-modal.component';
import { resolveRowPayload } from '../../shared/workspace-element-context';
import { AdminScreenContextService } from '../../admin/shared/admin-screen-context.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-user-modules',
    templateUrl: './user-modules.component.html',
    styleUrls: ['./user-modules.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UserModulesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  rowData: any = {};
  @Output() emitEvent = new EventEmitter<string>();

  private readonly subs = new Subscription();

  user_id = '';
  logged_in_user = '';
  loading = false;
  userAssignNav: any[] = [];
  filteredUserAssignNav: any[] = [];
  allActiveClients: any[] = [];
  allJurisdictions: any[] = [];
  allTemplates: any[] = [];
  allTemplatesList: any[] = [];
  selectedTemplates: any[] = [];

  client_key = 0;
  jurisdictionkey = 0;
  templatekey = 0;
  isReadyToApplyTemplate = false;
  userPreviouslyHadModules = false;
  showValidationErrors = false;
  userMessage = 'Select Template using following filters: "Client" -> "Jurisdiction" -> "Template"';
  searchTerm = '';
  selectedApplication: any = null;
  enableModuleAssign = '';
  allExpanded = false;

  columns: any[][] = [[], [], []];
  private selectedTemplateIds = new Set<any>();

  applicationFilter: any = {
    values: [],
    selected: [],
    name: 'Application(s)',
  };

  private globalParams: any = {};

  constructor(
    private appService: AppService,
    private userAdminService: UserAdminService,
    private alertService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private globalService: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private utilityService: UtilityService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private adminScreenContext: AdminScreenContextService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.bootstrapFromRow();
      })
    );
    this.subs.add(
      this.globalService.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.bootstrapFromRow();
      })
    );
    this.bootstrapFromRow();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const raw = resolveRowPayload(this.adminScreenContext, this.gtwWorkspace);
    this.rowData = Array.isArray(raw)
      ? (raw[0] && typeof raw[0] === 'object' ? raw[0] : {})
      : (raw || {});
  }

  private bootstrapFromRow(): void {
    const nextUserId = this.rowData?.SSO_ID || this.rowData?.SSO || this.rowData?.user_id || this.rowData?.sso_id || '';
    if (!nextUserId) {
      return;
    }
    if (nextUserId === this.user_id) {
      this.globalParams = { ...this.globalService.global?.globalParams };
      this.cdr.markForCheck();
      return;
    }
    this.user_id = nextUserId;
    const userSettings = this.appService.USER_SETTINGS;
    this.logged_in_user = userSettings?.user?.sso_id || '';
    this.client_key = userSettings?.user?.client_key || 0;
    this.globalParams = { ...this.globalService.global?.globalParams };
    this.enableModuleAssign = this.appService.SERVER_CONFIG?.configSettings?.enable_module_assign || 'true';

    this.getAllActiveBusinesses();
    this.cdr.markForCheck();
  }

  private getAllActiveBusinesses(): void {
    this.loading = true;
    const data = { sso_id: this.logged_in_user, selected_user_sso_id: this.user_id };
    this.userAdminService.getAllActiveBusinesses(data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        if (response?.callSuccess === '1') {
          this.allActiveClients = this.jsonObjectFactory.arrObjToLower(response.jsonObject);
          if (this.allActiveClients.length <= 0) {
            this.alertService.openSnackBar(
              'No Clients assigned to the selected user. Assign Client before assigning Modules',
              'gtw-snackbar--yellow'
            );
            this.userMessage = 'WARNING!! No Clients assigned to the selected user.';
          } else {
            this.userMessage = 'Please Select Business from following drop down.';
            this.getJurisByClientKey();
            this.getExistingUserTemplateDetails();
          }
        } else {
          this.alertService.openSnackBar(
            'An error has occurred. If you continue to experience this issue please visit the help page.',
            'gtw-snackbar--red'
          );
        }
        this.cdr.markForCheck();
      },
      () => { this.loading = false; this.cdr.markForCheck(); }
    );
  }

  getJurisByClientKey(): void {
    this.allJurisdictions = [];
    this.allTemplates = [];
    this.loading = true;

    const data = { clientkey: this.client_key };
    this.userAdminService.getJurisByBusiness(data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        if (response?.callSuccess === '1') {
          this.allJurisdictions = this.jsonObjectFactory.arrObjToLower(response.jsonObject);
          if (this.allJurisdictions.length > 0) {
            const us = _.find(this.allJurisdictions, { jurisdictionkey: 1 });
            this.jurisdictionkey = us ? 1 : this.allJurisdictions[0].jurisdictionkey;
            this.getTemplatesByJAndC(true);
          } else {
            this.userMessage = 'No Jurisdictions found. Please change Filter Criteria.';
          }
        } else {
          this.alertService.openSnackBar('Error loading jurisdictions.', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      () => { this.loading = false; this.cdr.markForCheck(); }
    );
  }

  getTemplatesByJAndC(autoSelection: boolean): void {
    this.allTemplates = [];
    this.allTemplatesList = [];
    this.selectedTemplates = [];
    this.selectedTemplateIds.clear();
    this.loading = true;

    const data = {
      action_code: 'p6bn0u',
      jurisdictionKey: this.jurisdictionkey,
      clientKey: this.client_key,
    };
    this.userAdminService.getTemplatesByJAndClient(data, 'p6bn0u').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        this.getData();
        if (response?.callSuccess === '1') {
          this.allTemplates = this.jsonObjectFactory.arrObjToLower(response.jsonObject);
          if (this.allTemplates.length > 0) {
            this.allTemplatesList = this.allTemplates.map((t: any) => ({
              id: t.templatekey,
              label: t.templatename,
            }));
            this.isReadyToApplyTemplate = true;
            this.userMessage = 'Select Template from Template drop down and Click Apply Template for bulk assignment of Modules. OR Select appropriate ROLE against each activity to individually apply Modules.';
          } else {
            this.userMessage = 'No Templates found for selected filter criteria.';
            this.isReadyToApplyTemplate = false;
          }
        }
        this.cdr.markForCheck();
      },
      () => { this.loading = false; this.cdr.markForCheck(); }
    );
  }

  private getData(): void {
    this.userAssignNav = [];
    this.loading = true;

    const data: any = { client_key: this.client_key, user_id: this.user_id };
    data.jurisdiction_key = this.jurisdictionkey || 1;

    this.userAdminService.getUserAssign(data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        if (response?.callSuccess === '1') {
          this.userAssignNav = response.jsonObject || [];
          this.applicationFilter.values = this.userAssignNav.map((item: any) => ({
            ...item,
            value: item.id,
          }));
          this.applicationFilter.selected = [...this.applicationFilter.values];
          this.mergeNonMatchedTaskHierarchy(this.userAssignNav);
          this.initializeDefaultRoles(this.userAssignNav);
          this.setDefaultCollapsed(this.userAssignNav);
          this.cacheNodeMetadata(this.userAssignNav);
          this.updateModuleCounts();

          const dropdownOption = _.find(this.userAssignNav, { id: '5' });
          this.setApplication(dropdownOption || null);
          this.filterMenu();
          this.refreshExpandedState();

          this.userMessage = 'Select Template from Template drop down and Click Apply Template for bulk assignment of Modules. OR Select appropriate ROLE against each activity to individually apply Modules.';
        } else {
          this.alertService.openSnackBar('Error loading module assignments.', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      () => { this.loading = false; this.cdr.markForCheck(); }
    );
  }

  private getExistingUserTemplateDetails(): void {
    if (!this.user_id) return;
    this.loading = true;

    const data = { userSsoId: this.user_id, client_key: this.client_key, action_code: '9et55j' };
    this.userAdminService.getUserTemplateDetails(data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        if (response?.callSuccess === '1') {
          const returnedData = this.jsonObjectFactory.arrObjToLower(response.jsonObject);
          if (returnedData.length > 0) {
            this.client_key = returnedData[0].client_key;
            this.jurisdictionkey = returnedData[0].jurisdictionkey;
            this.templatekey = returnedData[0].templatekey;
            this.userPreviouslyHadModules = true;
            this.userMessage = 'WARNING!! Selected user already has Template Modules.';
          }
        }
        this.cdr.markForCheck();
      },
      () => { this.loading = false; this.cdr.markForCheck(); }
    );
  }

  setApplication(item: any): void {
    this.searchTerm = '';
    if (item) {
      this.selectedApplication = item;
      this.filteredUserAssignNav = item.children || [];
    } else {
      this.selectedApplication = null;
      this.filteredUserAssignNav = this.applicationFilter.selected;
    }
    this.distributeToColumns();
    this.refreshExpandedState();
  }

  filterMenu(searchTerm?: string): void {
    const term = searchTerm || this.searchTerm;
    if (!term) {
      if (this.selectedApplication) {
        this.filteredUserAssignNav = this.selectedApplication.children || [];
      } else {
        this.filteredUserAssignNav = this.applicationFilter.selected;
      }
    } else {
      const termLower = term.toLowerCase();
      const matchName = (name: string) => (name || '').toLowerCase().includes(termLower);
      const filter = (arr: any[]): any[] => {
        if (!Array.isArray(arr)) return [];
        return arr.filter((item: any) => {
          if (Array.isArray(item.children)) {
            item.children = filter(item.children);
          }
          return matchName(item.name) || (item.children && item.children.length);
        });
      };

      if (this.selectedApplication) {
        this.filteredUserAssignNav = filter(_.cloneDeep(this.selectedApplication.children || []));
      } else {
        this.filteredUserAssignNav = filter(_.cloneDeep(this.applicationFilter.selected));
      }
    }
    this.distributeToColumns();
    this.refreshExpandedState();
  }

  private distributeToColumns(): void {
    this.columns = [[], [], []];
    if (this.filteredUserAssignNav) {
      for (let i = 0; i < this.filteredUserAssignNav.length; i++) {
        this.columns[i % 3].push(this.filteredUserAssignNav[i]);
      }
    }
  }

  private refreshExpandedState(): void {
    this.allExpanded = this.filteredUserAssignNav?.some((n: any) => !n.collapsed) ?? false;
  }

  toggleAll(): void {
    const shouldCollapse = this.allExpanded;
    for (const node of this.filteredUserAssignNav) {
      node.collapsed = shouldCollapse;
    }
    this.allExpanded = !shouldCollapse;
  }

  toggleNode(node: any): void {
    node.collapsed = !node.collapsed;
    this.refreshExpandedState();
  }

  trackByNodeId(index: number, node: any): any {
    return node.id ?? index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  onRoleSelectChange(child: any, selectedValue: string): void {
    child.role = child.roles.find((r: any) => String(r.value) === String(selectedValue)) || child.roles[0];
    this.onRoleChange(child);
  }

  onRoleChange(node: any): void {
    if (!node.role || !node.jcd_ta_key) return;

    this.userAdminService.setAccess(
      { jcd_ta_key: node.jcd_ta_key, role: node.role },
      this.user_id,
      0,
      this.client_key
    );
    this.updateModuleCounts();
  }

  changeGroupRole(roleHigh: string, node: any): void {
    if (!roleHigh) return;

    const updated: any[] = [];

    const getUpdated = (rh: string, group: any) => {
      if (Array.isArray(group.children)) {
        for (const item of group.children) {
          getUpdated(rh, item);
        }
      }
      if (Array.isArray(group.roles) && group.roles.length > 0) {
        if (rh === '1') {
          const maxLevel = Math.max(...group.roles.map((r: any) => parseInt(r.level, 10)));
          group.role = group.roles.find((r: any) => parseInt(r.level, 10) === maxLevel);
          updated.push(group);
        } else if (rh === '-1') {
          const nonZero = group.roles.filter((r: any) => r.level !== '0' && parseInt(r.level, 10) !== 0);
          if (nonZero.length > 0) {
            const minLevel = Math.min(...nonZero.map((r: any) => parseInt(r.level, 10)));
            group.role = group.roles.find((r: any) => parseInt(r.level, 10) === minLevel);
            updated.push(group);
          }
        } else {
          group.role = group.roles.find((r: any) => r.level === '0' || parseInt(r.level, 10) === 0);
          updated.push(group);
        }
      }
    };

    getUpdated(roleHigh, node);

    const params = updated.map(n => ({
      jcd_ta_key: n.jcd_ta_key,
      jcd_ta_role_key: n.role.value,
      user_id: this.user_id,
      client_key: this.client_key
    }));

    if (params.length > 0) {
      this.userAdminService.setAccessAll(JSON.stringify(params), this.user_id, 0, this.client_key);
    }

    this.updateModuleCounts();
    node._bulkDropdown = '';
  }

  openUserAccessModal(node: any): void {
    const dialogRef = this.dialog.open(UserAssignModalComponent, {
      width: '600px',
      disableClose: true,
      panelClass: ['assign-user-dialog', 'increase-z-index'],
      position: { top: '25px' },
      data: { taskKey: node.id }
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result?.saved) {
        this.getData();
      }
      this.cdr.markForCheck();
    });
  }

  isTemplateSelected(templateId: any): boolean {
    return this.selectedTemplateIds.has(templateId);
  }

  toggleTemplateSelection(template: any): void {
    if (this.selectedTemplateIds.has(template.id)) {
      this.selectedTemplateIds.delete(template.id);
      this.selectedTemplates = this.selectedTemplates.filter((t: any) => t.id !== template.id);
    } else {
      this.selectedTemplateIds.add(template.id);
      this.selectedTemplates.push(template);
    }
  }

  validateBeforeApplyTemplate(): void {
    if (!this.isReadyToApplyTemplate) {
      this.alertService.openSnackBar(
        'Select Business -> Jurisdiction -> Template and Try to Apply Template',
        'gtw-snackbar--yellow'
      );
      return;
    }

    this.loading = true;
    const userSettings = this.appService.USER_SETTINGS;
    const clobData: any = { users: [this.user_id], templates: [], isFinalList: 'Y' };

    if (this.selectedTemplates.length > 0) {
      this.selectedTemplates.forEach((t: any) => clobData.templates.push(t.id));
    }

    this.userAdminService.saveMultiTempUser(
      clobData,
      userSettings?.user?.client?.client_key || this.client_key,
      this.globalParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        const data = response?.data || response;
        if (data?.callSuccess === '1') {
          this.alertService.openSnackBar('Templates assigned successfully', 'gtw-snackbar--green');
          this.showValidationErrors = false;
          this.getData();
        } else {
          this.alertService.openSnackBar('Error applying template.', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      () => {
        this.loading = false;
        this.alertService.openSnackBar('Error applying template.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  confirmRemoveAllAccess(): void {
    if (!confirm('Are you sure you want to remove all module access for this user?')) {
      return;
    }
    this.removeAllAccess();
  }

  removeAllAccess(): void {
    const data = { user_sso_id: this.user_id };
    this.userAdminService.removeAllAccess(data);
    this.alertService.openSnackBar('Access removal requested.', 'gtw-snackbar--green');
    setTimeout(() => { this.getData(); this.cdr.markForCheck(); }, 1500);
  }

  compareRole(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return String(a.value) === String(b.value);
  }

  updateModuleCounts(): void {
    for (const app of this.userAssignNav) {
      this.computeCounts(app);
    }
  }

  private computeCounts(node: any): { assigned: number; total: number } {
    if (!node.children || node.children.length === 0) {
      node._assigned = 0;
      node._total = 0;
      node._badgeClass = 'badge-partial';
      return { assigned: 0, total: 0 };
    }

    let assigned = 0;
    let total = 0;

    for (const child of node.children) {
      if (child?.role && child.role.value > 0) {
        assigned++;
      }
      if (child?.role) {
        total++;
      }
      if (child?.children?.length > 0) {
        const childCounts = this.computeCounts(child);
        assigned += childCounts.assigned;
        total += childCounts.total;
      } else if (child) {
        child._assigned = 0;
        child._total = 0;
        child._badgeClass = 'badge-partial';
      }
    }

    node._assigned = assigned;
    node._total = total;
    node._badgeClass = (assigned === total && total > 0) ? 'badge-complete' : 'badge-partial';
    return { assigned, total };
  }

  private cacheNodeMetadata(nodes: any[]): void {
    for (const node of nodes) {
      node._tooltip = (node.ownerList?.length > 0)
        ? node.ownerList.map((u: any) => u.name).join(', ')
        : 'No Users Assigned';
      if (node.children) {
        this.cacheNodeMetadata(node.children);
      }
    }
  }

  private initializeDefaultRoles(nodes: any[]): void {
    for (const node of nodes) {
      if (node.roles?.length > 0) {
        if (!node.role) {
          node.role = node.roles[0];
        } else {
          const match = node.roles.find((r: any) => String(r.value) === String(node.role.value));
          if (match) {
            node.role = match;
          }
        }
      }
      if (node.children) {
        this.initializeDefaultRoles(node.children);
      }
    }
  }

  private setDefaultCollapsed(nodes: any[]): void {
    for (const node of nodes) {
      node.collapsed = true;
      node._bulkDropdown = '';
      if (node.children) {
        this.setDefaultCollapsed(node.children);
      }
    }
  }

  private mergeNonMatchedTaskHierarchy(jsonAry: any[]): void {
    for (let i = 0; i < jsonAry.length; i++) {
      const chlObj = jsonAry[i];
      if (chlObj.children && chlObj.children.length) {
        for (let c = 0; c < chlObj.children.length; c++) {
          const childTaskObj = chlObj.children[c];
          const childObjList = _.filter(chlObj.children, { parentTaskKey: childTaskObj.id });
          if (childObjList.length) {
            childTaskObj.processed = true;
            for (const childObj of childObjList) {
              if (childTaskObj.children) {
                childTaskObj.children.unshift(childObj);
              } else {
                childTaskObj.children = [childObj];
              }
            }
          }
        }
      }
      chlObj.children = _.filter(chlObj.children, { parentTaskKey: null });
    }
  }
}
